import React, { useEffect, useState } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getProductBySlug, storeProducts, isAvailable } from '../data/storeProducts';

const StoreProduct: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const product = slug ? getProductBySlug(slug) : undefined;

  const [activeImage, setActiveImage] = useState(0);
  const [selectedSize, setSelectedSize] = useState<number | null>(null);

  useEffect(() => {
    setActiveImage(0);
    setSelectedSize(product && product.sizes.length === 1 ? 0 : null);
    window.scrollTo({ top: 0, behavior: 'instant' as ScrollBehavior });
  }, [slug, product]);

  if (!product) {
    return <Navigate to="/store" replace />;
  }

  const available = isAvailable(product);

  const relatedProducts = storeProducts
    .filter((p) => p.slug !== product.slug && p.category === product.category)
    .slice(0, 4);

  const handleBuy = () => {
    if (selectedSize === null) return;
    const url = product.sizes[selectedSize].cartUrl;
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="bg-black min-h-screen text-white selection:bg-white selection:text-black">
      <div className="h-20" />

      {/* Breadcrumb */}
      <div className="border-b border-white/10">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-5">
          <nav className="text-[11px] uppercase tracking-[0.2em] text-neutral-gray-500">
            <Link to="/store" className="hover:text-white transition-colors duration-300">
              Store
            </Link>
            <span className="mx-3 text-neutral-gray-700">/</span>
            <span className="text-white">
              {product.name}
              {product.color ? ` (${product.color})` : ''}
            </span>
          </nav>
        </div>
      </div>

      {/* Main product layout */}
      <section className="relative">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12 lg:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
            {/* Image column */}
            <div>
              <motion.div
                key={activeImage}
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="relative aspect-[4/5] bg-[#0E1422] border border-white/[0.08] rounded-2xl overflow-hidden"
              >
                <img
                  src={product.images[activeImage]}
                  alt={product.name}
                  className="absolute inset-0 w-full h-full object-cover"
                />
              </motion.div>

              {/* Thumbnails */}
              {product.images.length > 1 && (
                <div className="mt-4 grid grid-cols-4 gap-3">
                  {product.images.map((img, idx) => (
                    <button
                      key={img}
                      onClick={() => setActiveImage(idx)}
                      className={`relative aspect-square bg-[#0E1422] overflow-hidden rounded-xl border transition-all duration-300 ${
                        activeImage === idx
                          ? 'border-white'
                          : 'border-white/10 hover:border-white/40 opacity-70 hover:opacity-100'
                      }`}
                    >
                      <img src={img} alt="" className="absolute inset-0 w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>

            {/* Details column */}
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="lg:sticky lg:top-28 lg:self-start"
            >
              <p className="text-[10px] uppercase tracking-[0.3em] text-neutral-gray-500 mb-5">
                Enhance Companies
              </p>

              <h1 className="font-display font-black text-3xl sm:text-4xl lg:text-5xl leading-[1] tracking-[-0.02em]">
                {product.name}
              </h1>
              {product.color && (
                <p className="mt-3 text-lg lg:text-xl text-neutral-gray-300 font-display">
                  ({product.color})
                </p>
              )}

              {product.price !== null ? (
                <p className="mt-8 text-2xl font-display font-semibold tabular-nums">
                  ${product.price}
                </p>
              ) : (
                <p className="mt-8 text-sm uppercase tracking-[0.25em] text-neutral-gray-400">
                  Pricing to be announced
                </p>
              )}

              <div className="my-10 h-px bg-white/10" />

              <p className="text-neutral-gray-300 text-base leading-relaxed">
                {product.description}
              </p>

              {available ? (
                <>
                  {/* Size selector */}
                  <div className="mt-10">
                    <div className="flex items-baseline justify-between mb-4">
                      <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-gray-400">
                        {product.sizes.length === 1 ? 'Fit' : 'Select Size'}
                      </p>
                      {selectedSize !== null && product.sizes.length > 1 && (
                        <p className="text-[10px] uppercase tracking-[0.2em] text-neutral-gray-500">
                          {product.sizes[selectedSize].label} selected
                        </p>
                      )}
                    </div>

                    <div
                      className={`grid gap-2 ${
                        product.sizes.length === 1 ? 'grid-cols-1' : 'grid-cols-4'
                      }`}
                    >
                      {product.sizes.map((size, idx) => (
                        <button
                          key={size.label}
                          onClick={() => setSelectedSize(idx)}
                          className={`py-4 text-xs font-semibold uppercase tracking-[0.15em] border rounded-xl transition-all duration-300 ${
                            selectedSize === idx
                              ? 'border-white bg-white text-black'
                              : 'border-white/20 text-white hover:border-white/60'
                          }`}
                        >
                          {size.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Buy button */}
                  <button
                    onClick={handleBuy}
                    disabled={selectedSize === null}
                    className={`mt-8 w-full py-5 text-[11px] font-semibold uppercase tracking-[0.3em] rounded-xl transition-all duration-500 ${
                      selectedSize !== null
                        ? 'bg-white text-black hover:bg-neutral-gray-200 hover:tracking-[0.35em]'
                        : 'bg-white/10 text-neutral-gray-500 cursor-not-allowed'
                    }`}
                  >
                    {selectedSize !== null ? 'Go to Checkout' : 'Select a Size'}
                  </button>

                  <p className="mt-4 text-[11px] text-neutral-gray-500 text-center leading-relaxed">
                    Checkout completes securely via Shopify.
                  </p>
                </>
              ) : (
                <div className="mt-10">
                  <div className="relative border border-white/20 rounded-2xl p-8 bg-gradient-to-br from-white/[0.04] to-white/[0.01] overflow-hidden">
                    {/* Corner ticks */}
                    <span className="pointer-events-none absolute top-3 left-3 w-3 h-3 border-t border-l border-white/40" />
                    <span className="pointer-events-none absolute top-3 right-3 w-3 h-3 border-t border-r border-white/40" />
                    <span className="pointer-events-none absolute bottom-3 left-3 w-3 h-3 border-b border-l border-white/40" />
                    <span className="pointer-events-none absolute bottom-3 right-3 w-3 h-3 border-b border-r border-white/40" />

                    <div className="text-center">
                      <p className="text-[10px] uppercase tracking-[0.45em] text-neutral-gray-400">
                        Coming Soon
                      </p>
                      <div className="my-4 mx-auto w-10 h-px bg-white/30" />
                      <p className="text-white text-base leading-relaxed max-w-sm mx-auto">
                        This piece is part of an upcoming drop. Reach out to be
                        notified the moment it launches.
                      </p>
                      <Link
                        to="/contact"
                        className="mt-7 inline-flex items-baseline gap-3 text-[11px] uppercase tracking-[0.3em] text-white border-b border-white/30 hover:border-white pb-1 transition-all duration-500"
                      >
                        <span>Request Updates</span>
                        <span>→</span>
                      </Link>
                    </div>
                  </div>
                </div>
              )}

              {/* Details */}
              <div className="mt-12 space-y-px">
                {[
                  { label: 'Shipping', value: 'Ships within 3 to 5 business days.' },
                  { label: 'Care', value: 'Cold wash, hang dry. Do not iron over branding.' },
                ].map((item) => (
                  <div
                    key={item.label}
                    className="flex flex-col sm:flex-row sm:items-start gap-2 sm:gap-8 py-5 border-t border-white/10 last:border-b"
                  >
                    <p className="text-[10px] uppercase tracking-[0.25em] text-neutral-gray-500 sm:w-28 flex-shrink-0">
                      {item.label}
                    </p>
                    <p className="text-sm text-neutral-gray-300 leading-relaxed">{item.value}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Related */}
      {relatedProducts.length > 0 && (
        <section className="border-t border-white/10">
          <div className="max-w-7xl mx-auto px-6 lg:px-12 py-20">
            <p className="text-[10px] uppercase tracking-[0.3em] text-neutral-gray-500 mb-3">
              Continue browsing
            </p>
            <h2 className="font-display font-black text-3xl lg:text-4xl tracking-[-0.02em] mb-12">
              More in this category
            </h2>

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-6 gap-y-10">
              {relatedProducts.map((p) => (
                <Link key={p.slug} to={`/store/${p.slug}`} className="group block">
                  <div className="relative aspect-[4/5] bg-[#0E1422] border border-white/[0.08] group-hover:border-white/25 rounded-2xl overflow-hidden transition-colors duration-500">
                    <img
                      src={p.images[0]}
                      alt={p.name}
                      className="absolute inset-0 w-full h-full object-cover transition-transform duration-[900ms] ease-[cubic-bezier(0.22,1,0.36,1)] group-hover:scale-[1.04]"
                      loading="lazy"
                    />
                    {p.images[1] && (
                      <img
                        src={p.images[1]}
                        alt=""
                        className="absolute inset-0 w-full h-full object-cover opacity-0 group-hover:opacity-100 transition-opacity duration-[900ms]"
                        loading="lazy"
                      />
                    )}
                  </div>
                  <div className="mt-4 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <h3 className="text-white font-display font-semibold text-sm leading-snug">
                        {p.name}
                      </h3>
                      {p.color && (
                        <p className="mt-1 text-neutral-gray-400 text-xs leading-snug">
                          ({p.color})
                        </p>
                      )}
                    </div>
                    {p.price !== null ? (
                      <p className="text-white font-display font-semibold text-sm tabular-nums whitespace-nowrap">
                        ${p.price}
                      </p>
                    ) : (
                      <p className="text-neutral-gray-500 text-[10px] uppercase tracking-[0.2em] whitespace-nowrap">
                        TBD
                      </p>
                    )}
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
};

export default StoreProduct;
